import { failure, fold, inProgress, notAsked, RemoteData, success } from 'ngx-remotedata';
import { ErrorWrapper, OrderFormComment } from '@trade-platform/ui-shared';
import { OrderFormCommentActions, OrderFormCommentActionTypes } from './actions';

export type OrderFormCommentState = RemoteData<OrderFormComment, ErrorWrapper>;

const initialState = notAsked<OrderFormComment, ErrorWrapper>();

export const orderFormCommentReducer = (
    state: OrderFormCommentState = initialState,
    action: OrderFormCommentActions
): OrderFormCommentState => {
    switch (action.type) {
        case OrderFormCommentActionTypes.ORDER_FORM_COMMENT_GET:
            return inProgress({} as OrderFormComment);
        case OrderFormCommentActionTypes.ORDER_FORM_COMMENT_GET_SUCCESS:
            return success(action.payload);
        case OrderFormCommentActionTypes.ORDER_FORM_COMMENT_GET_FAILURE:
            return failure(action.payload.error);

        case OrderFormCommentActionTypes.ORDER_FORM_COMMENT_CREATE:
            return inProgress({} as OrderFormComment);
        case OrderFormCommentActionTypes.ORDER_FORM_COMMENT_CREATE_SUCCESS:
            return success(action.payload);
        case OrderFormCommentActionTypes.ORDER_FORM_COMMENT_CREATE_FAILURE:
            return failure(action.payload.error);

        case OrderFormCommentActionTypes.ORDER_FORM_COMMENT_EDIT:
            return inProgress({} as OrderFormComment);
        case OrderFormCommentActionTypes.ORDER_FORM_COMMENT_EDIT_SUCCESS:
            return success(action.payload);

        case OrderFormCommentActionTypes.ORDER_FORM_COMMENT_DELETE:
            return inProgress({} as OrderFormComment);
        case OrderFormCommentActionTypes.ORDER_FORM_COMMENT_DELETE_SUCCESS:
            return success(action.payload);
        case OrderFormCommentActionTypes.ORDER_FORM_COMMENT_DELETE_FAILURE:
            return failure(action.payload.error);

        case OrderFormCommentActionTypes.ORDER_FORM_COMMENT_UPDATE:
            return inProgress({} as OrderFormComment);
        case OrderFormCommentActionTypes.ORDER_FORM_COMMENT_UPDATE_SUCCESS:
            return success(action.payload);
        case OrderFormCommentActionTypes.ORDER_FORM_COMMENT_UPDATE_FAILURE:
            return failure(action.payload.error);

        case OrderFormCommentActionTypes.ORDER_FORM_COMMENT_RESOLVE:
            return inProgress({} as OrderFormComment);
        case OrderFormCommentActionTypes.ORDER_FORM_COMMENT_RESOLVE_SUCCESS:
            return success(action.payload);
        case OrderFormCommentActionTypes.ORDER_FORM_COMMENT_RESOLVE_FAILURE:
            return failure(action.payload.error);

        default:
            return state;
    }
};

export type OrderFormCommentsState = RemoteData<OrderFormComment[], ErrorWrapper>;
const initialState2 = notAsked<OrderFormComment[], ErrorWrapper>();

/**
 * Reads the comment list out of any state. `getOrElse` only unwraps Success, but resolving moves the
 * state to InProgress first, and the list has to survive that in order to be merged into.
 */
const currentComments = (state: OrderFormCommentsState): OrderFormComment[] =>
    fold<OrderFormComment[], ErrorWrapper>(
        () => [],
        value => value || [],
        (_error, value) => value || [],
        value => value,
        state
    );

/**
 * Copies the new status of each resolved comment onto the list we already hold.
 *
 * The resolve endpoint answers with one raw comment row per request. Only the comments *list*
 * endpoint computes `isOnForm` and `isRemoved`, so those responses cannot carry them — replacing the
 * list with them would drop the flags and every comment that was not resolved, leaving consumers that
 * read the store directly (e.g. the form list's "N Items" badge) looking at a list that no longer
 * describes the order. `status` is the only thing the resolve response is authoritative about.
 *
 * Kept in step with the maintenance copy in
 * libs/advisor-ui/maintenance/src/lib/store/order-comments/reducer.ts — maintenance renders these same
 * process components with MaintenanceOrdersStoreFacade, so both stores need this behaviour.
 */
const applyResolvedStatuses = (
    current: OrderFormComment[],
    resolvedResponses: OrderFormComment[]
): OrderFormComment[] => {
    const newStatusById = new Map<number, string>();
    (resolvedResponses || []).forEach(resolved => {
        if (resolved && resolved.id !== undefined && resolved.id !== null) {
            newStatusById.set(resolved.id, resolved.status);
        }
    });

    if (newStatusById.size === 0) {
        return current;
    }

    const withNewStatus = (comment: OrderFormComment): OrderFormComment => {
        const updated: OrderFormComment = { ...comment };
        const newStatus = newStatusById.get(comment.id);

        if (newStatus !== undefined) {
            updated.status = newStatus;
        }
        // A reply can be resolved on its own.
        if (comment.children && comment.children.length > 0) {
            updated.children = comment.children.map(withNewStatus);
        }

        return updated;
    };

    return current.map(withNewStatus);
};

export const orderFormCommentsReducer = (
    state: OrderFormCommentsState = initialState2,
    action: OrderFormCommentActions
): OrderFormCommentsState => {
    switch (action.type) {
        case OrderFormCommentActionTypes.ORDER_FORM_COMMENT_LIST:
            return inProgress([]);
        case OrderFormCommentActionTypes.ORDER_FORM_COMMENT_LIST_SUCCESS:
            return success(action.payload);
        case OrderFormCommentActionTypes.ORDER_FORM_COMMENT_LIST_FAILURE:
            return failure(action.payload.error);

        case OrderFormCommentActionTypes.ORDER_FORM_COMMENTS_RESOLVE:
            // Hold on to the decorated list while the resolve requests are in flight — it is the only
            // copy that carries isOnForm/isRemoved.
            return inProgress(currentComments(state));
        case OrderFormCommentActionTypes.ORDER_FORM_COMMENTS_RESOLVE_SUCCESS:
            return success(applyResolvedStatuses(currentComments(state), action.payload.comments));
        case OrderFormCommentActionTypes.ORDER_FORM_COMMENTS_RESOLVE_FAILURE:
            return failure(action.payload.error);

        default:
            return state;
    }
};
